/**
 * Contact Information Component
 * 
 * Main container for displaying all contact methods available to reach the business.
 * This component:
 * - Renders the contact header with title and description
 * - Defines SVG icons for each contact method (email, phone, location)
 * - Contains an array of contact items (email, phone, address, social media)
 * - Maps through contact items to render individual ContactItem components
 * 
 * @component
 * @returns {JSX.Element} Complete contact information section with header and contact items
 */

import React from 'react';
import ContactHeader from '../contact-header/contact-header';
import ContactItem from '../contact-item/contact-item';
import './contact-info-style.css';

const ContactInfo = () => {
  /**
   * Email Icon Component
   * SVG icon representing an email envelope
   * @returns {JSX.Element} Email icon SVG
   */
  const EmailIcon = () => (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M22 6L12 13L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  /**
   * Phone Icon Component
   * SVG icon representing a telephone
   * @returns {JSX.Element} Phone icon SVG
   */
  const PhoneIcon = () => (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4741 21.8325 20.7293C21.7209 20.9845 21.5573 21.2136 21.3521 21.4018C21.1469 21.5901 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3146 6.72533 15.2661 5.18999 12.85C3.49997 10.2412 2.44824 7.27097 2.11999 4.17997C2.09501 3.90344 2.12788 3.62474 2.21649 3.3616C2.3051 3.09846 2.44756 2.85666 2.63476 2.6516C2.82196 2.44653 3.0498 2.28268 3.30379 2.1705C3.55777 2.05831 3.83233 2.00024 4.10999 1.99997H7.10999C7.5953 1.99522 8.06579 2.16705 8.43376 2.48351C8.80173 2.79996 9.04207 3.23942 9.10999 3.71997C9.23662 4.68004 9.47144 5.6227 9.80999 6.52997C9.94454 6.8879 9.97366 7.27689 9.8939 7.65086C9.81415 8.02482 9.62886 8.36809 9.35999 8.63998L8.08999 9.90997C9.51355 12.4135 11.5864 14.4864 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9751 14.1858 16.3491 14.1061C16.7231 14.0263 17.1121 14.0554 17.47 14.19C18.3773 14.5285 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  /**
   * Location Icon Component
   * SVG icon representing a map pin/location marker
   * Used for physical address and social media links
   * @returns {JSX.Element} Location icon SVG
   */
  const LocationIcon = () => (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  /**
   * Array of contact items
   * Each item contains:
   * @property {JSX.Element} icon - SVG icon component for the contact method
   * @property {string} title - Display name of the contact method
   * @property {string} content - The actual contact information (email, phone, address, etc.)
   * @property {string|null} link - URL or action link (mailto:, tel:, social media URL, or null)
   */
  const contactItems = [
    {
      icon: <EmailIcon />,
      title: 'Email',
      content: 'gemevm@gmail.com',
      link: 'mailto: gemevm@gmail.com' // Opens default email client
    },
    {
      icon: <PhoneIcon />,
      title: 'Teléfono',
      content: '+506 89105489',
      link: 'tel:+50689105489' // Enables click-to-call on mobile devices
    },
    {
      icon: <LocationIcon />,
      title: 'Ubicación',
      content: 'Alajuela, Orotina Centro, entrada vieja a Orotina, 400 m norte de las antiguas oficinas del ICE.',
      link: null // No link for address (map is shown separately)
    }
  ];

  return (
    <div className="contact-info">
      {/* Contact page header with title and description */}
      <ContactHeader />
      {/* Container for all contact method items */}
      <div className="contact-info__items">
        {/* Map through contact items and render each one */}
        {contactItems.map((item, index) => (
          <ContactItem
            key={index}
            icon={item.icon}
            title={item.title}
            content={item.content}
            link={item.link}
          />
        ))}
      </div>
    </div>
  );
};

export default ContactInfo;
